/* eslint-disable no-unused-vars */
import axios from 'axios';
import { ACCESS_TOKEN_KEY } from 'helper/init.constants';
import { getAccessToken, getRefreshToken, parseJwt } from 'helper/utils';
import { ROUTE_PATH } from 'routes/route.constant';

const http = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 3000, // default timeout
});

let refreshTokenRequest = null;
const wait = (ms) => new Promise((res) => setTimeout(res, ms));

http.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken() ?? '';
    const refreshToken = getRefreshToken();
    const timeExpiredAccessToken = parseJwt(accessToken)?.exp;

    const handleRefreshToken = () =>
      axios.post(`${process.env.REACT_APP_API}/auth/refresh`, undefined, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });

    if (
      config.url?.includes('/users/import') ||
      config.url?.includes('/files') ||
      config.url?.includes('/ceus/import')
    ) {
      config.timeout = 10 * 3600 * 1000;
    }

    if (timeExpiredAccessToken * 1000 < Date.now()) {
      refreshTokenRequest = refreshTokenRequest
        ? refreshTokenRequest
        : handleRefreshToken();
      const result = await refreshTokenRequest;

      refreshTokenRequest = null;
      localStorage.setItem(ACCESS_TOKEN_KEY, JSON.stringify(result.data.data));
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${result.data.data.accessToken}`,
      };
      return config;
    }

    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };

    return config;
  },
  () => Promise.resolve()
);

http.interceptors.response.use(
  (response) => {
    return {
      ...response.data,
      isOk: true,
    };
  },

  async (error) => {
    const isNetworkError = !error?.response;
    const isTimeout = error?.code === 'ECONNABORTED';
    const isCanceled = axios.isCancel(error);
    const requestUrl = error?.config?.url || '';
    const isUserImportApi = requestUrl.includes('/users/import');
    const isCeusImportApi = requestUrl.includes('/ceus/import');

    const IGNORE_TOAST_URLS = [
      {
        method: 'delete',
        pattern: /^\/seminars\/[\w-]+$/,
      },
      { method: 'post', pattern: /^\/users\/import$/ },
      { method: 'post', pattern: /^\/ceus\/import$/ },
    ];

    if (isNetworkError || isTimeout || isCanceled) {
      await wait(500);
      const defaultErrorMessage =
        '処理に失敗しました。もう一度お試しください。';
      return Promise.reject({
        isOk: false,
        code: error?.code || 'REQUEST_FAILED',
        message: defaultErrorMessage,
      });
    }

    const status = error?.response?.status;

    switch (status) {
      case 401: {
        localStorage.clear();
        window.location.href = ROUTE_PATH.LOGIN;
        break;
      }
      default: {
        await wait(500);
        const errorData = {
          isOk: false,
          message:
            error?.response?.data?.message ||
            error?.message ||
            '処理に失敗しました。もう一度お試しください。',
          code: status,
        };

        const { method, url } = error.config || {};
        const shouldIgnore = IGNORE_TOAST_URLS.some(
          (item) => item.method === method && item.pattern.test(url || '')
        );

        if (!shouldIgnore && !isUserImportApi && !isCeusImportApi) {
          return Promise.reject(errorData.message);
        }
        return Promise.reject(errorData);
      }
    }
  }
);

export default http;
